import React, { FC } from "react";

import { Box, Flexbox } from "ui-library/layouts";
import { Button, Icon, SimpleLoader, Typography } from "ui-library/atoms";

import i18n from "core/i18n";
import { FileItem, FileItemStatusEnum } from "types";

import { useFileUploadField } from "./hooks/useFileUploadField";

export interface FileListProps {
    name: string;
    disabled?: boolean;
    displayWhenEmpty?: boolean;
    onDeleteFile?: (file: FileItem) => Promise<void>;
}

export const FileList: FC<FileListProps> = ({ name, disabled = false, displayWhenEmpty = true, onDeleteFile }) => {
    const { t } = i18n.useTranslation();
    const { files, removeFile } = useFileUploadField(name, { onDelete: onDeleteFile });
    const isEmpty = files.length === 0;

    if (isEmpty && !displayWhenEmpty) return null;

    return (
        <>
            <Box mt={2} mb={1}>
                <Typography variant="caption" color="gray">
                    {t("common.uploadedFiles")}
                </Typography>
            </Box>

            {isEmpty && <Typography variant="p">{t("common.noFilesUploaded")}</Typography>}

            {files.map((file: FileItem) => {
                const canBeRemoved = file.uploadStatus !== FileItemStatusEnum.PENDING;
                const isInFailedState = [FileItemStatusEnum.ERROR_ON_DELETE, FileItemStatusEnum.ERROR_ON_UPLOAD].includes(
                    file.uploadStatus,
                );

                return (
                    <Flexbox key={file.id} gap={1} alignItems={"center"} style={{ height: "42px" }}>
                        <Icon icon={isInFailedState ? "circle-error" : "document-check"} color={isInFailedState ? "red" : "success"} />

                        <Typography>{file.fileName}</Typography>

                        {file.uploadStatus === FileItemStatusEnum.PENDING && <SimpleLoader loading={true} />}

                        {!disabled && canBeRemoved && (
                            <Button onClick={() => removeFile(file)} variant="text" color="blue">
                                {t("common.remove")}
                            </Button>
                        )}
                    </Flexbox>
                );
            })}
        </>
    );
};
